import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Icon, Skeleton, SkeletonForm } from '@repo/ui';
import { Button, Input, Label, Card, CardHeader, CardBody, LastUpdated } from '@repo/ui';
import { FolderCreationTest, CustomFieldManager, JuniperDataCard } from '../components';
import { showToast } from '@repo/ui';
import { archivalApi } from '@xtnd/web/features/archival/api/archivalApi';
import type { ArchivalConfiguration } from '@xtnd/web/features/archival/types/archivalType';
import { api } from '@xtnd/web/shared/api';
import { EmailVerificationButton } from '@xtnd/web/features/settings/components';
import { useSaveJuniperConfigMutation } from '../queries';
import { clioIntegrationApi } from '@xtnd/web/features/settings/api/clioIntegrationApi';

interface EmailEntry {
  email: string;
  verificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  lastChecked?: Date;
}

interface JuniperConfigData {
  fromEmailAddress: string;
  fromEmailVerified: boolean;
  fromEmailVerificationStatus?: 'not_started' | 'pending_verification' | 'verified' | 'failed';
  replyToEmailAddress?: string;
  invoiceTemplate: Array<{ templateId: string; default: boolean; name: string }>;
  emailTemplateId: string;
  emailSubjectLine: string;
  operationalEmailSubject: string;
  trustRequestEmailSubject: string;
  emailBcc: string[];
  // Email Override Configuration
  overrideClientEmails: boolean;
  overrideEmailAddress: string;
  // Clio Folder Structure - Fixed Names
  clioMatterRootFolderName: string; // Always "XTND"
  clioMatterReceiptsFolderName: string; // Always "Juniper-Receipts"
  clioMatterInvoicesFolderName: string; // Always "Juniper-Invoices"
  clioMatterReceiptsOutboxFolderName: string; // Always "Receipts-Outbox"
  clioMatterReceiptsFiledFolderName: string; // Always "Receipts-Filed"
  // Clio Folder Structure - Configurable Templates
  clioMatterReceiptsFiledSubfolderTemplate: string; // Template for receipt filed subfolder
  clioMatterInvoicePdfFilenameTemplate: string; // Template for invoice PDF filename
  clioMatterLedgerPdfFilenameTemplate: string; // Template for ledger PDF filename
  // Test Configuration
  clioTestMatterId: string; // Matter ID for testing folder creation
  autoSendEmails: boolean;
  autoApproveTrustRequests: boolean;
  enableTrustRequestRetry: boolean;
  enableTrustRequestVoid: boolean;
  invoiceReportTemplateId: string;
  ledgerReportTemplateId: string;
  // Bank account configuration
  defaultTrustBankAccountId?: string;
  defaultOperationalBankAccountId?: string;
  archivalServiceId: string;
  enableExternalArchival: boolean;
  archivalFileNamePattern: string;
  archivalFolderName?: string; // Optional archival folder name
  escalationEmails: EmailEntry[];
  slackWebhookPath: string;
  queryCacheTtlMinutes: number;
  // Email Receipts Configuration
  enableEmailReceipts: boolean;
  emailReceiptsConfig: {
    allowedTypes: string[];
    maxTotalSizeBytes: number;
    maxFilesPerEmail: number;
    outboxFolderName: string;
    filedFolderName: string;
    sendingFolderSuffix: string;
    enableValidation: boolean;
    enableCleanup: boolean;
  };
  // Ledger Report Configuration
  includeLedgerInEmail: boolean;
}

interface Template {
  id: string;
  name: string;
  type: 'pdf' | 'email';
  description?: string;
}

// Email validation utility
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function JuniperConfigPage() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Bank accounts state
  const [trustBankAccounts, setTrustBankAccounts] = useState<any[]>([]);
  const [operationalBankAccounts, setOperationalBankAccounts] = useState<any[]>([]);

  const [config, setConfig] = useState<JuniperConfigData>({
    fromEmailAddress: '',
    fromEmailVerified: false,
    fromEmailVerificationStatus: 'not_started',
    replyToEmailAddress: '',
    invoiceTemplate: [],
    emailTemplateId: '',
    emailSubjectLine: 'Your Juniper Transaction #{juniperId}',
    operationalEmailSubject: 'Operational Invoice - #{operationalBillNumber}',
    trustRequestEmailSubject: 'Trust Replenishment Request - #{trustRequestNumber}',
    emailBcc: [],
    // Email Override Configuration
    overrideClientEmails: false,
    overrideEmailAddress: '',
    // Clio Folder Structure - Fixed Names (lowercase to match ensureJuniperFolderStructure)
    clioMatterRootFolderName: 'xtnd',
    clioMatterReceiptsFolderName: 'juniper-receipts',
    clioMatterInvoicesFolderName: 'juniper-invoices',
    clioMatterReceiptsOutboxFolderName: 'juniper-receipt-outbox',
    clioMatterReceiptsFiledFolderName: 'juniper-receipt-filed',
    // Clio Folder Structure - Configurable Templates
    clioMatterReceiptsFiledSubfolderTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}',
    clioMatterInvoicePdfFilenameTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Invoice.pdf',
    clioMatterLedgerPdfFilenameTemplate:
      '{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Ledger.pdf',
    // Test Configuration
    clioTestMatterId: '',
    autoSendEmails: true,
    autoApproveTrustRequests: false,
    enableTrustRequestRetry: true,
    enableTrustRequestVoid: true,
    invoiceReportTemplateId: 'template-standard',
    ledgerReportTemplateId: '',
    archivalServiceId: '',
    enableExternalArchival: false,
    archivalFileNamePattern: '{juniperId}-juniper-data-{timestamp}.json',
    escalationEmails: [{ email: 'ops@xtnd.com', verificationStatus: 'not_started' }],
    slackWebhookPath: '/xtnd/slack-webhook',
    queryCacheTtlMinutes: 15,
    // Email Receipts Configuration
    enableEmailReceipts: false,
    emailReceiptsConfig: {
      allowedTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'],
      maxTotalSizeBytes: 25 * 1024 * 1024, // 25MB
      maxFilesPerEmail: 10,
      outboxFolderName: 'juniper-receipt-outbox',
      filedFolderName: 'juniper-receipt-filed',
      sendingFolderSuffix: '_sending',
      enableValidation: true,
      enableCleanup: true,
    },
    // Ledger Report Configuration
    includeLedgerInEmail: true,
    // Bank account configuration
    defaultTrustBankAccountId: undefined,
    defaultOperationalBankAccountId: undefined,
  });

  // Fetch current configuration
  const { data: currentConfig, isLoading } = useQuery<JuniperConfigData>({
    queryKey: ['juniper-config'],
    queryFn: async () => {
      // Use the JWT-based endpoint that extracts tenant ID from the token
      const response = await api.get('/juniper/config');
      return response as JuniperConfigData;
    },
    retry: 3,
    retryDelay: 1000,
  });

  // Fetch available templates
  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      // Use JWT-based endpoint that extracts tenant ID from token
      const data = (await api.get('/templates/config')) as any;
      const allTemplates: Template[] = [];

      // Handle the nested object structure returned by the API
      if (data.templates) {
        // Process PDF templates
        if (data.templates.pdf && typeof data.templates.pdf === 'object') {
          Object.entries(data.templates.pdf).forEach(([templateId, template]: [string, any]) => {
            allTemplates.push({
              id: templateId,
              name: template.templateName || template.name || templateId,
              type: 'pdf',
              description: template.description || template.templateName || template.name,
            });
          });
        }

        // Process Email templates
        if (data.templates.email && typeof data.templates.email === 'object') {
          Object.entries(data.templates.email).forEach(([templateId, template]: [string, any]) => {
            allTemplates.push({
              id: templateId,
              name: template.templateName || template.name || templateId,
              type: 'email',
              description: template.description || template.templateName || template.name,
            });
          });
        }
      }

      return allTemplates;
    },
  });

  // Fetch archival configurations
  const { data: archivalConfigs = [] } = useQuery<ArchivalConfiguration[]>({
    queryKey: ['archival-configs'],
    queryFn: async () => {
      // Use JWT-based endpoint that extracts tenant ID from token
      const response = await archivalApi.getConfigurations();
      return response;
    },
  });

  // Update configuration mutation
  const saveMutation = useSaveJuniperConfigMutation();

  // Initialize form with current config when loaded
  useEffect(() => {
    if (currentConfig) {
      setConfig(prev => ({
        ...prev,
        ...currentConfig,
        // Normalize escalationEmails to EmailEntry[]
        escalationEmails: (currentConfig.escalationEmails || []).map((entry: any) => {
          if (typeof entry === 'string') {
            return { email: entry, verificationStatus: 'not_started' } as EmailEntry;
          }
          if (entry && typeof entry === 'object') {
            return {
              email: entry.email || '',
              verificationStatus: entry.verificationStatus,
              lastChecked: entry.lastChecked ? new Date(entry.lastChecked) : undefined,
            } as EmailEntry;
          }
          return { email: '', verificationStatus: 'not_started' } as EmailEntry;
        }),
      }));
    }
  }, [currentConfig]);

  // Fetch bank accounts on component mount
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const [trustAccounts, operationalAccounts] = await Promise.all([
          clioIntegrationApi.getBankAccounts('trust'),
          clioIntegrationApi.getBankAccounts('operational'),
        ]);
        setTrustBankAccounts(trustAccounts);
        setOperationalBankAccounts(operationalAccounts);
      } catch (error) {
        console.warn('Failed to fetch bank accounts:', error);
        showToast.error('Failed to load bank accounts for configuration');
      }
    };

    fetchBankAccounts();
  }, []);

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!config.fromEmailAddress.trim()) {
      errors.fromEmailAddress = 'From Email Address is required';
    } else if (!isValidEmail(config.fromEmailAddress)) {
      errors.fromEmailAddress = 'Please enter a valid email address';
    }

    if (!config.emailSubjectLine.trim()) {
      errors.emailSubjectLine = 'Email Subject is required';
    }

    if (!config.emailTemplateId) {
      errors.emailTemplateId = 'Email Template is required';
    }

    // Clio Integration required fields - configurable templates only
    if (!config.clioMatterReceiptsFiledSubfolderTemplate.trim()) {
      errors.clioMatterReceiptsFiledSubfolderTemplate =
        'Receipt Filed Subfolder Template is required';
    }

    if (!config.clioMatterInvoicePdfFilenameTemplate.trim()) {
      errors.clioMatterInvoicePdfFilenameTemplate = 'Invoice PDF Filename Template is required';
    }

    if (!config.clioMatterLedgerPdfFilenameTemplate.trim()) {
      errors.clioMatterLedgerPdfFilenameTemplate = 'Ledger PDF Filename Template is required';
    }

    // BCC Email validation
    config.emailBcc.forEach((email, index) => {
      if (email.trim() && !isValidEmail(email)) {
        errors[`emailBcc_${index}`] = 'Please enter a valid email address';
      }
    });

    // Escalation Email validation (handle string or object)
    config.escalationEmails.forEach((emailEntry: any, index) => {
      const emailValue = typeof emailEntry === 'string' ? emailEntry : emailEntry?.email || '';
      if (emailValue.trim() && !isValidEmail(emailValue)) {
        errors[`escalationEmail[${index}]`] = 'Please enter a valid email address';
      }
    });

    // Override Email validation
    if (config.overrideClientEmails) {
      if (!config.overrideEmailAddress.trim()) {
        errors.overrideEmailAddress = 'Override Email Address is required when override is enabled';
      } else if (!isValidEmail(config.overrideEmailAddress)) {
        errors.overrideEmailAddress = 'Please enter a valid email address';
      }
    }

    // Reply-To Email validation
    if (config.replyToEmailAddress?.trim()) {
      if (!isValidEmail(config.replyToEmailAddress)) {
        errors.replyToEmailAddress = 'Please enter a valid email address';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast.error('Please fix validation errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      await saveMutation.mutateAsync(config);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFromEmailChange = (email: string) => {
    setConfig(prev => ({
      ...prev,
      fromEmailAddress: email,
      // Remove verified status if email changed
      fromEmailVerified: email === prev.fromEmailAddress ? prev.fromEmailVerified : false,
    }));
  };

  const handleTestFolderCreation = async (matterId: string) => {
    try {
      const result = (await api.post('/juniper/test-folder-creation', {
        matterId: parseInt(matterId),
        config: {
          rootFolderName: config.clioMatterRootFolderName,
          receiptsFolderName: config.clioMatterReceiptsFolderName,
          invoicesFolderName: config.clioMatterInvoicesFolderName,
          receiptsOutboxFolderName: config.clioMatterReceiptsOutboxFolderName,
          receiptsFiledFolderName: config.clioMatterReceiptsFiledFolderName,
          receiptsFiledSubfolderTemplate: config.clioMatterReceiptsFiledSubfolderTemplate,
          invoicePdfFilenameTemplate: config.clioMatterInvoicePdfFilenameTemplate,
          ledgerPdfFilenameTemplate: config.clioMatterLedgerPdfFilenameTemplate,
        },
      })) as any;

      return {
        success: true,
        matterId,
        xtndFolderId: result.folderStructure.xtndFolderId,
        testTransactionFolderName: result.testTransactionFolderName,
      };
    } catch (error: any) {
      return {
        success: false,
        matterId,
        error: error.message,
      };
    }
  };

  const addBccEmail = () => {
    setConfig(prev => ({
      ...prev,
      emailBcc: [...prev.emailBcc, ''],
    }));
  };

  const updateBccEmail = (index: number, value: string) => {
    setConfig(prev => ({
      ...prev,
      emailBcc: prev.emailBcc.map((email, i) => (i === index ? value : email)),
    }));
  };

  const removeBccEmail = (index: number) => {
    setConfig(prev => ({
      ...prev,
      emailBcc: prev.emailBcc.filter((_, i) => i !== index),
    }));
  };

  const handleAddEscalationEmail = () => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: [
        ...prev.escalationEmails,
        { email: '', verificationStatus: 'not_started' },
      ],
    }));
  };

  const handleUpdateEscalationEmail = (index: number, value: string) => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: prev.escalationEmails.map((emailEntry, i) =>
        i === index
          ? {
              ...emailEntry,
              email: value,
              verificationStatus:
                emailEntry.email !== value ? 'not_started' : emailEntry.verificationStatus,
            }
          : emailEntry
      ),
    }));
  };

  const handleRemoveEscalationEmail = (index: number) => {
    setConfig(prev => ({
      ...prev,
      escalationEmails: prev.escalationEmails.filter((_, i) => i !== index),
    }));
  };

  // Filter templates - ensure only PDFs for PDF dropdowns
  const pdfTemplates = templates.filter(t => t.type === 'pdf');
  const emailTemplates = templates.filter(t => t.type === 'email');
  const archivalServices = archivalConfigs?.filter((ac: ArchivalConfiguration) => ac.enabled) || [];

  // Check if configuration is complete for "Configured" status
  const isConfigured = !!(
    config.fromEmailAddress &&
    config.fromEmailVerified &&
    config.emailSubjectLine &&
    config.emailTemplateId &&
    config.clioMatterReceiptsFiledSubfolderTemplate &&
    config.clioMatterInvoicePdfFilenameTemplate &&
    config.clioMatterLedgerPdfFilenameTemplate
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width="300px" height="2rem" />
          </div>
          <Skeleton variant="rectangular" width="120px" height="40px" />
        </div>

        {/* Form Skeleton */}
        <Card variant="default">
          <CardBody>
            <SkeletonForm fields={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div
              className={`w-3 h-3 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-yellow-500'}`}
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {isConfigured ? 'Configured' : 'Configuration Incomplete'}
            </span>
          </div>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
            className="bg-xtnd-blue hover:bg-xtnd-blue/90 text-white"
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <Icon name="HiCog" className="h-8 w-8 text-xtnd-blue" />
          <div>
            <h1 className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-white">
              Juniper Configuration
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Configure trust request processing, email notifications, and integration settings
            </p>
          </div>
        </div>
        <LastUpdated className="mt-1" timestamp={(currentConfig as any)?.updatedAt} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Juniper Data Card */}
        <div className="lg:col-span-2">
          <JuniperDataCard />
        </div>

        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Email Configuration
            </h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="fromEmail">From Email Address *</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="fromEmail"
                  type="email"
                  autoComplete="email"
                  value={config.fromEmailAddress}
                  onChange={e => handleFromEmailChange(e.target.value)}
                  placeholder="juniper@yourfirm.com"
                  className={`flex-1 ${validationErrors.fromEmailAddress ? 'border-red-500' : ''}`}
                  required
                />
                <EmailVerificationButton
                  email={config.fromEmailAddress}
                  verificationStatus={config.fromEmailVerificationStatus}
                  onStatusChange={(_, status) => {
                    setConfig(prev => ({
                      ...prev,
                      fromEmailVerificationStatus: status,
                      fromEmailVerified: status === 'verified',
                    }));
                  }}
                />
              </div>
              {validationErrors.fromEmailAddress && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.fromEmailAddress}</p>
              )}
              {config.fromEmailVerificationStatus === 'verified' && (
                <p className="text-sm text-green-600 mt-1">✅ Verified - Can receive emails</p>
              )}
              {config.fromEmailVerificationStatus === 'pending_verification' && (
                <p className="text-sm text-yellow-600 mt-1">
                  ⏳ Pending - Check your email for verification link
                </p>
              )}
              {config.fromEmailVerificationStatus === 'failed' && (
                <p className="text-sm text-red-600 mt-1">❌ Verification failed - Try again</p>
              )}
            </div>

            <div>
              <Label htmlFor="replyToEmail">Reply-To Email Address</Label>
              <Input
                id="replyToEmail"
                type="email"
                autoComplete="email"
                value={config.replyToEmailAddress || ''}
                onChange={e => setConfig(prev => ({ ...prev, replyToEmailAddress: e.target.value }))}
                placeholder="support@yourfirm.com"
                className={validationErrors.replyToEmailAddress ? 'border-red-500' : ''}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Optional: Alternative email address for customer replies on Juniper emails.
                If not set, replies will go to the From Email Address above.
              </p>
              {validationErrors.replyToEmailAddress && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.replyToEmailAddress}</p>
              )}
            </div>

            <div>
              <Label htmlFor="operationalEmailSubject">Email Subject Templates</Label>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="operationalEmailSubject" className="text-sm font-medium">
                    Operational Direct Subject *
                  </Label>
                  <Input
                    id="operationalEmailSubject"
                    value={config.operationalEmailSubject}
                    onChange={e => setConfig(prev => ({ ...prev, operationalEmailSubject: e.target.value }))}
                    placeholder="Operational Invoice - #{operationalBillNumber}"
                    className={`w-full ${validationErrors.operationalEmailSubject ? 'border-red-500' : ''}`}
                    required
                  />
                  {validationErrors.operationalEmailSubject && (
                    <p className="text-sm text-red-600 mt-1">{validationErrors.operationalEmailSubject}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Variables: {'{{'}operationalBillNumber{'}'}, {'{{'}operationalInvoiceId{'}'}, {'{{'}juniperId{'}'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="trustRequestEmailSubject" className="text-sm font-medium">
                    Trust Request (Evergreen) Subject *
                  </Label>
                  <Input
                    id="trustRequestEmailSubject"
                    value={config.trustRequestEmailSubject}
                    onChange={e => setConfig(prev => ({ ...prev, trustRequestEmailSubject: e.target.value }))}
                    placeholder="Trust Replenishment Request - #{trustRequestNumber}"
                    className={`w-full ${validationErrors.trustRequestEmailSubject ? 'border-red-500' : ''}`}
                    required
                  />
                  {validationErrors.trustRequestEmailSubject && (
                    <p className="text-sm text-red-600 mt-1">{validationErrors.trustRequestEmailSubject}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Variables: {'{{'}trustRequestNumber{'}'}, {'{{'}trustRequestId{'}'}, {'{{'}juniperId{'}'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="emailTemplate">Email Template *</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/template-manager')}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                  className="text-xtnd-blue hover:text-xtnd-blue/80"
                >
                  Add Template
                </Button>
              </div>
              <select
                id="emailTemplate"
                value={config.emailTemplateId}
                onChange={e => setConfig(prev => ({ ...prev, emailTemplateId: e.target.value }))}
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white ${validationErrors.emailTemplateId ? 'border-red-500' : ''}`}
                required
              >
                <option value="">Select template...</option>
                {emailTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {validationErrors.emailTemplateId && (
                <p className="text-sm text-red-600 mt-1">{validationErrors.emailTemplateId}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  BCC Recipients
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addBccEmail}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                >
                  Add BCC
                </Button>
              </div>
              {config.emailBcc.map((email, index) => (
                <div key={index} className="flex items-center space-x-2 mt-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={e => updateBccEmail(index, e.target.value)}
                    placeholder="bcc@yourfirm.com"
                    className={`flex-1 ${validationErrors[`emailBcc_${index}`] ? 'border-red-500' : ''}`}
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeBccEmail(index)}>
                    <Icon name="HiTrash" className="h-4 w-4 text-red-500" />
                  </Button>
                  {validationErrors[`emailBcc_${index}`] && (
                    <p className="text-sm text-red-600">{validationErrors[`emailBcc_${index}`]}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoSendEmails"
                checked={config.autoSendEmails}
                onChange={e => setConfig(prev => ({ ...prev, autoSendEmails: e.target.checked }))}
                className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="autoSendEmails">
                Auto-send emails (uncheck to hold for manual release)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enableEmailReceipts"
                checked={config.enableEmailReceipts}
                onChange={e =>
                  setConfig(prev => ({ ...prev, enableEmailReceipts: e.target.checked }))
                }
                className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="enableEmailReceipts">Enable Email Receipts Processing</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="includeLedgerInEmail"
                checked={config.includeLedgerInEmail}
                onChange={e =>
                  setConfig(prev => ({ ...prev, includeLedgerInEmail: e.target.checked }))
                }
                className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
              />
              <Label htmlFor="includeLedgerInEmail">Include Ledger Report in Email Attachments</Label>
            </div>
          </CardBody>
        </Card>

        {/* Templates and Adapters Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Templates and Adapters
            </h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pdfTemplate">Juniper Invoice Template</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/template-manager')}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                  className="text-xtnd-blue hover:text-xtnd-blue/80"
                >
                  Add Template
                </Button>
              </div>
              <select
                id="pdfTemplate"
                value={config.invoiceReportTemplateId}
                onChange={e =>
                  setConfig(prev => ({ ...prev, invoiceReportTemplateId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select Juniper invoice template...</option>
                {pdfTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="ledgerReportTemplate">Ledger Report</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/template-manager')}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                  className="text-xtnd-blue hover:text-xtnd-blue/80"
                >
                  Add Template
                </Button>
              </div>
              <select
                id="ledgerReportTemplate"
                value={config.ledgerReportTemplateId}
                onChange={e =>
                  setConfig(prev => ({ ...prev, ledgerReportTemplateId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select ledger report template...</option>
                {pdfTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="trustRequestTemplate">Trust Request Template</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/template-manager')}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                  className="text-xtnd-blue hover:text-xtnd-blue/80"
                >
                  Add Template
                </Button>
              </div>
              <select
                id="trustRequestTemplate"
                value={config.trustRequestTemplateId}
                onChange={e =>
                  setConfig(prev => ({ ...prev, trustRequestTemplateId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select trust request template...</option>
                {pdfTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="archivalService">Archival Service</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/archival')}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                  className="text-xtnd-blue hover:text-xtnd-blue/80"
                >
                  Add Service
                </Button>
              </div>
              <select
                id="archivalService"
                value={config.archivalServiceId}
                onChange={e => setConfig(prev => ({ ...prev, archivalServiceId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select archival service...</option>
                {archivalServices.map((service: ArchivalConfiguration) => (
                  <option key={service.serviceId} value={service.serviceId}>
                    {service.metadata?.description || `${service.serviceType} Service`} (
                    {service.serviceType})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableExternalArchival"
                  checked={config.enableExternalArchival}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, enableExternalArchival: e.target.checked }))
                  }
                  className="w-4 h-4 text-xtnd-blue focus:ring-xtnd-blue border-gray-300 rounded"
                />
                <Label htmlFor="enableExternalArchival">Enable External Archival</Label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Automatically archive Juniper transaction data to external storage
              </p>
            </div>

            {config.enableExternalArchival && (
              <>
                <div>
                  <Label htmlFor="archivalFileNamePattern">File Naming Pattern</Label>
                  <Input
                    id="archivalFileNamePattern"
                    value={config.archivalFileNamePattern}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, archivalFileNamePattern: e.target.value }))
                    }
                    placeholder="{juniperId}-juniper-data-{timestamp}.json"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available placeholders: <code>{'{juniperId}'}</code>,{' '}
                    <code>{'{tenantId}'}</code>, <code>{'{timestamp}'}</code>,{' '}
                    <code>{'{date}'}</code>, <code>{'{time}'}</code>,{' '}
                    <code>{'{operationalInvoiceId}'}</code>, <code>{'{trustRequestId}'}</code>
                  </p>
                  <div className="mt-2 p-2 bg-gray-50 text-green-900 dark:bg-gray-800 dark:text-green-400 rounded text-xs">
                    <strong>Preview:</strong>{' '}
                    {config.archivalFileNamePattern
                      .replace(/{juniperId}/g, 'JUN-abc123')
                      .replace(/{tenantId}/g, '0000000001')
                      .replace(/{timestamp}/g, '2024-01-15-14-30-00')
                      .replace(/{date}/g, '2024-01-15')
                      .replace(/{time}/g, '14-30-00')
                      .replace(/{operationalInvoiceId}/g, 'OP-456')
                      .replace(/{trustRequestId}/g, 'TR-789')}
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="archivalFolderName">Archive Folder Name (Optional)</Label>
                  <Input
                    id="archivalFolderName"
                    value={config.archivalFolderName || ''}
                    onChange={e => {
                      const value = e.target.value;
                      // Validate folder name on change
                      if (value === '' || /^[a-zA-Z0-9_-]*$/.test(value)) {
                        setConfig(prev => ({ ...prev, archivalFolderName: value }));
                      }
                    }}
                    placeholder="juniper-invoices"
                    maxLength={50}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Specify a folder within your storage container/bucket. Leave empty to store in
                    root.
                    <br />
                    <strong>Allowed:</strong> Letters, numbers, hyphens (-), and underscores (_). No
                    spaces or special characters.
                  </p>
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs border border-blue-200 text-green-900 dark:text-green-400 dark:bg-blue-900/20 dark:border-blue-800">
                    <strong>Storage Path Preview:</strong>
                    <br />• Azure Blob:{' '}
                    <code>container/{config.archivalFolderName || 'root'}/file.json</code>
                    <br />• AWS S3:{' '}
                    <code>bucket/{config.archivalFolderName || 'root'}/file.json</code>
                    <br />• OneDrive:{' '}
                    <code>drive/{config.archivalFolderName || 'root'}/file.json</code>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* Clio Matter Folder Integration Settings */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Clio Matter Folder Integration
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure automatic folder creation and file organization in Clio matters
            </p>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Folder Structure Display */}
            <div className="bg-gray-50 dark:bg-xtnd-dark-700 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-3">
                Folder Structure Preview
              </h3>
              <div className="text-sm font-mono text-gray-700 dark:text-gray-300 space-y-1">
                <div>📁 Matter Root Folder/</div>
                <div className="ml-4">└── 📁 {config.clioMatterRootFolderName}/</div>
                <div className="ml-8">├── 📁 {config.clioMatterReceiptsFolderName}/</div>
                <div className="ml-12">│ ├── 📁 {config.clioMatterReceiptsOutboxFolderName}/</div>
                <div className="ml-12">│ └── 📁 {config.clioMatterReceiptsFiledFolderName}/</div>
                <div className="ml-16">
                  │ └── 📁 {config.clioMatterReceiptsFiledSubfolderTemplate}/
                </div>
                <div className="ml-8">└── 📁 {config.clioMatterInvoicesFolderName}/</div>
                <div className="ml-12">├── 📄 {config.clioMatterInvoicePdfFilenameTemplate}</div>
                <div className="ml-12">└── 📄 {config.clioMatterLedgerPdfFilenameTemplate}</div>
              </div>
            </div>

            {/* Configurable Templates */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                Configurable Templates
              </h3>

              <div>
                <Label htmlFor="receiptFiledSubfolder">Receipt Filed Subfolder Template *</Label>
                <Input
                  id="receiptFiledSubfolder"
                  value={config.clioMatterReceiptsFiledSubfolderTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterReceiptsFiledSubfolderTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}"
                  className={`w-full ${validationErrors.clioMatterReceiptsFiledSubfolderTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterReceiptsFiledSubfolderTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterReceiptsFiledSubfolderTemplate}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Folder name for organizing receipts by transaction. Variables: {'{'}juniperId{'}'}
                  , {'{'}trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>

              <div>
                <Label htmlFor="invoicePdfTemplate">Invoice PDF Filename Template *</Label>
                <Input
                  id="invoicePdfTemplate"
                  value={config.clioMatterInvoicePdfFilenameTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterInvoicePdfFilenameTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Invoice.pdf"
                  className={`w-full ${validationErrors.clioMatterInvoicePdfFilenameTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterInvoicePdfFilenameTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterInvoicePdfFilenameTemplate}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Filename for generated invoice PDFs. Variables: {'{'}juniperId{'}'}, {'{'}
                  trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>

              <div>
                <Label htmlFor="ledgerPdfTemplate">Ledger PDF Filename Template *</Label>
                <Input
                  id="ledgerPdfTemplate"
                  value={config.clioMatterLedgerPdfFilenameTemplate}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      clioMatterLedgerPdfFilenameTemplate: e.target.value,
                    }))
                  }
                  placeholder="{juniperId}-{trustRequestNumber}-{operationalInvoiceNumber}-Ledger.pdf"
                  className={`w-full ${validationErrors.clioMatterLedgerPdfFilenameTemplate ? 'border-red-500' : ''}`}
                  size="lg"
                />
                {validationErrors.clioMatterLedgerPdfFilenameTemplate && (
                  <p className="text-sm text-red-600 mt-1">
                    {validationErrors.clioMatterLedgerPdfFilenameTemplate}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Filename for generated ledger PDFs. Variables: {'{'}juniperId{'}'}, {'{'}
                  trustRequestNumber{'}'}, {'{'}operationalInvoiceNumber{'}'}
                </p>
              </div>
            </div>

            {/* Test Configuration */}
            <div className="space-y-4 border-t pt-4 dark:border-xtnd-dark-600">
              <h3 className="text-lg font-medium text-xtnd-dark dark:text-xtnd-white">
                Folder Creation
              </h3>
              <FolderCreationTest onTestFolder={handleTestFolderCreation} />
            </div>

            {/* Information */}
            <div className="bg-blue-50 dark:bg-xtnd-dark-600 p-4 rounded-lg border border-blue-200 dark:border-xtnd-dark-500">
              <div className="flex items-start space-x-3">
                <Icon
                  name="HiInformationCircle"
                  className="h-5 w-5 text-blue-600 dark:text-xtnd-blue mt-0.5"
                />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-xtnd-white">
                    Folder Structure Notes
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-gray-300 mt-2 space-y-1">
                    <li>• Folders marked with 📁 are automatically created as needed</li>
                    <li>• Fixed folder names (xtnd, juniper-receipts, etc.) cannot be modified</li>
                    <li>• Only the templates above can be customized with variables</li>
                    <li>
                      • Receipts in the Outbox folder are automatically included in the next invoice
                      email
                    </li>
                    <li>• Filed receipts are organized by transaction in subfolders</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Custom Field Management */}
        <CustomFieldManager expectedFieldName="Enable Juniper Processing" />

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Advanced Settings
            </h2>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableRetry"
                  checked={config.enableTrustRequestRetry}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, enableTrustRequestRetry: e.target.checked }))
                  }
                  className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="enableRetry">Enable Trust Request Retry</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableVoid"
                  checked={config.enableTrustRequestVoid}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, enableTrustRequestVoid: e.target.checked }))
                  }
                  className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="enableVoid">Enable Trust Request Void Prior Logic</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="autoApproveTrustRequests"
                  checked={config.autoApproveTrustRequests}
                  onChange={e =>
                    setConfig(prev => ({ ...prev, autoApproveTrustRequests: e.target.checked }))
                  }
                  className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                />
                <Label htmlFor="autoApproveTrustRequests">Auto-approve Trust Requests</Label>
              </div>

              {/* Email Override Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="overrideClientEmails"
                    checked={config.overrideClientEmails}
                    onChange={e =>
                      setConfig(prev => ({ ...prev, overrideClientEmails: e.target.checked }))
                    }
                    className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue"
                  />
                  <Label htmlFor="overrideClientEmails" className="font-medium">
                    Override All Client Email Traffic
                  </Label>
                  <div className="group relative">
                    <Icon
                      name="HiInformationCircle"
                      className="h-4 w-4 text-gray-400 cursor-help"
                    />
                    <div className="absolute left-0 top-6 z-10 w-80 p-3 bg-gray-900 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <strong>Development/Testing Feature:</strong>
                      <br />
                      When enabled, ALL invoice emails will be sent ONLY to the override email
                      address below (plus any BCC recipients). Client email addresses will be
                      completely ignored. Use this for testing without sending emails to real
                      clients.
                    </div>
                  </div>
                </div>

                {config.overrideClientEmails && (
                  <div className="ml-6 space-y-2">
                    <div>
                      <Label htmlFor="overrideEmailAddress" className="text-sm">
                        Override Email Address
                      </Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <Input
                          type="email"
                          id="overrideEmailAddress"
                          value={config.overrideEmailAddress}
                          onChange={e =>
                            setConfig(prev => ({ ...prev, overrideEmailAddress: e.target.value }))
                          }
                          placeholder="test@yourfirm.com"
                          className={`flex-1 ${validationErrors['overrideEmailAddress'] ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {validationErrors['overrideEmailAddress'] && (
                        <p className="text-sm text-red-600 mt-1">
                          {validationErrors['overrideEmailAddress']}
                        </p>
                      )}
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
                      <div className="flex items-start space-x-2">
                        <Icon
                          name="HiInformationCircle"
                          className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
                        />
                        <div>
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Email Override Active
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                            All invoice emails will be sent to{' '}
                            <strong>{config.overrideEmailAddress || 'the address above'}</strong>{' '}
                            instead of client email addresses.
                            {config.emailBcc.length > 0 && (
                              <span>
                                {' '}
                                BCC recipients ({config.emailBcc.join(', ')}) will still receive
                                copies.
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Escalation Email Recipients
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAddEscalationEmail}
                  leftIcon={<Icon name="HiPlus" size="sm" />}
                >
                  Add Email
                </Button>
              </div>
              {config.escalationEmails.map((emailEntry, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="email"
                      value={emailEntry.email}
                      onChange={e => handleUpdateEscalationEmail(index, e.target.value)}
                      placeholder="ops@yourfirm.com"
                      className={`flex-1 ${validationErrors[`escalationEmail[${index}]`] ? 'border-red-500' : ''}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEscalationEmail(index)}
                    >
                      <Icon name="HiTrash" className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  {validationErrors[`escalationEmail[${index}]`] && (
                    <p className="text-sm text-red-600">
                      {validationErrors[`escalationEmail[${index}]`]}
                    </p>
                  )}
                  {/* Verification Status Message */}
                  {emailEntry.email &&
                    emailEntry.verificationStatus &&
                    emailEntry.verificationStatus !== 'not_started' && (
                      <div className="ml-8 text-xs">
                        {emailEntry.verificationStatus === 'verified' && (
                          <span className="text-green-600 dark:text-green-400">
                            ✅ Verified - Can receive escalation emails
                          </span>
                        )}
                        {emailEntry.verificationStatus === 'pending_verification' && (
                          <span className="text-yellow-600 dark:text-yellow-400">
                            ⏳ Pending - Check your email for verification link
                          </span>
                        )}
                        {emailEntry.verificationStatus === 'failed' && (
                          <span className="text-red-600 dark:text-red-400">
                            ❌ Verification failed - Try again
                          </span>
                        )}
                        {emailEntry.lastChecked && (
                          <span className="text-gray-500 dark:text-gray-400 ml-2">
                            (Last checked: {new Date(emailEntry.lastChecked).toLocaleTimeString()})
                          </span>
                        )}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Clio Payments Configuration */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Clio Payments Configuration
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure default bank accounts for operational and trust payment links
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="defaultTrustBankAccount">Default Destination Trust Bank Account</Label>
              <select
                id="defaultTrustBankAccount"
                value={config.defaultTrustBankAccountId || ''}
                onChange={e =>
                  setConfig(prev => ({ ...prev, defaultTrustBankAccountId: e.target.value || undefined }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select trust bank account...</option>
                {trustBankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} {account.account_number ? `(${account.account_number})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Used for trust request payment links when no specific account is configured
              </p>
            </div>

            <div>
              <Label htmlFor="defaultOperationalBankAccount">Default Destination Operational Bank Account</Label>
              <select
                id="defaultOperationalBankAccount"
                value={config.defaultOperationalBankAccountId || ''}
                onChange={e =>
                  setConfig(prev => ({ ...prev, defaultOperationalBankAccountId: e.target.value || undefined }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
              >
                <option value="">Select operational bank account...</option>
                {operationalBankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} {account.account_number ? `(${account.account_number})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Used for operational bill payment links when no specific account is configured
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Email Receipts Configuration Card */}
      {config.enableEmailReceipts && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-blue" />
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                Email Receipts Configuration
              </h2>
            </div>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
              Configure email receipts processing for trust request transactions
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="maxFilesPerEmail">
                Maximum Files Per Email
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Maximum number of files to attach to a single email
                </span>
              </Label>
              <Input
                id="maxFilesPerEmail"
                type="number"
                min="1"
                max="50"
                value={config.emailReceiptsConfig.maxFilesPerEmail}
                placeholder="10"
                readOnly
                className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="maxTotalSizeBytes">
                Maximum Total Size (MB)
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Maximum total size for all attachments
                </span>
              </Label>
              <Input
                id="maxTotalSizeBytes"
                type="number"
                min="1"
                max="25"
                value={Math.round(config.emailReceiptsConfig.maxTotalSizeBytes / (1024 * 1024))}
                placeholder="25"
                readOnly
                className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="outboxFolderName">
                Outbox Folder Name
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Folder name for documents to be processed
                </span>
              </Label>
              <Input
                id="outboxFolderName"
                value={config.emailReceiptsConfig.outboxFolderName}
                placeholder="juniper-receipt-outbox"
                readOnly
                className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="filedFolderName">
                Filed Folder Name
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                  Folder name for processed documents
                </span>
              </Label>
              <Input
                id="filedFolderName"
                value={config.emailReceiptsConfig.filedFolderName}
                placeholder="juniper-receipt-filed"
                readOnly
                className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableValidation"
                  checked={config.emailReceiptsConfig.enableValidation}
                  disabled
                  className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue opacity-50 cursor-not-allowed"
                />
                <Label htmlFor="enableValidation">Enable File Validation</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableCleanup"
                  checked={config.emailReceiptsConfig.enableCleanup}
                  disabled
                  className="rounded border-gray-300 dark:border-gray-600 text-xtnd-blue focus:ring-xtnd-blue dark:bg-xtnd-dark-700 dark:focus:ring-xtnd-blue opacity-50 cursor-not-allowed"
                />
                <Label htmlFor="enableCleanup">Enable Cleanup of Sending Folder</Label>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-xtnd-dark-700 p-3 rounded-md">
              <p className="text-sm text-blue-800 dark:text-gray-300">
                <strong>Note:</strong> Allowed file types and size limits are automatically
                configured from your tenant's email service configuration.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Juniper Query Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Icon name="HiClock" className="h-5 w-5 text-xtnd-blue" />
            <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
              Juniper Query Settings
            </h2>
          </div>
          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
            Configure caching and performance settings for live data queries
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="queryCacheTtl">
              Cache TTL (Minutes) *
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 ml-2">
                How long to cache live Clio data (1-60 minutes)
              </span>
            </Label>
            <Input
              id="queryCacheTtl"
              type="number"
              min="1"
              max="60"
              value={config.queryCacheTtlMinutes}
              onChange={e =>
                setConfig(prev => ({
                  ...prev,
                  queryCacheTtlMinutes: parseInt(e.target.value) || 15,
                }))
              }
              placeholder="15"
              className={validationErrors.queryCacheTtlMinutes ? 'border-red-500' : ''}
            />
            {validationErrors.queryCacheTtlMinutes && (
              <p className="text-sm text-red-600 mt-1">{validationErrors.queryCacheTtlMinutes}</p>
            )}
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-1">
              Lower values = More live data, higher API usage. Higher values = Better performance,
              less current data.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
